import { useEffect, useState } from 'react';
import { Icon, IconProps } from '../atoms/Icon';
import { Button as AriaButton } from 'react-aria-components';
import cx from 'classix';

type CommonProps = {
  children: React.ReactNode;
  icon?: IconProps['type'];
  /** If true, omits rendering the Accordion's border. Used to stack Accordions side-by-side without duplicate borders. */
  isBorderless?: boolean;
  divider?: boolean;
  /** If true, the accordion state begins closed */
  // initiallyClosed?: boolean;
};

type DefaultVariantProps = CommonProps & {
  type: 'default';
  title: string;
};

/** Allows specifying children for the accordion button bar content without predefined padding or styles. This still retains the up/down chevron.
 * Also omits the panel's content padding.
 *
 * If we continue making tweaks for this custom variant, we should split it into a fully separate component and rethink whether we're having consistent UX. The
 * current version is practically unformatted and just a thin wrapper around the concept of a closable panel w/ header.
 **/
type CustomVariantProps = CommonProps & {
  type: 'custom';
  className?: string;
  title?: string;
  leftContent?: React.ReactNode;
  titleContent?: React.ReactNode;
  rightContent?: React.ReactNode;
};

export const Accordion = (
  props: (DefaultVariantProps | CustomVariantProps) & { initiallyClosed?: boolean },
) => {
  const [isOpen, setIsOpen] = useState(!props.initiallyClosed);
  return <BaseAccordion {...props} isOpen={isOpen} setIsOpen={setIsOpen} />;
};

/** Accordion component which can be reactively opened via parent component while allowing for user click interaction */
export const ControlledAccordion = (
  props: (DefaultVariantProps | CustomVariantProps) & {
    isOpen: boolean;
  },
) => {
  const [accordionIsOpen, setAccordionIsOpen] = useState(props.isOpen);
  useEffect(() => {
    setAccordionIsOpen(props.isOpen);
  }, [props.isOpen]);
  return <BaseAccordion {...props} isOpen={accordionIsOpen} setIsOpen={setAccordionIsOpen} />;
};

const BaseAccordion = (
  props: (DefaultVariantProps | CustomVariantProps) & {
    isOpen: boolean;
    setIsOpen: (isOpen: boolean) => void;
  },
) => {
  const { isOpen, setIsOpen } = props;
  const leftContent = (props.type === 'custom' && props.leftContent) ?? undefined;
  const titleContent = (props.type === 'custom' && props.titleContent) ?? undefined;
  const rightContent = (props.type === 'custom' && props.rightContent) ?? undefined;

  return (
    // Technically the mocks do not call for a standalone Accordion that has its own border. But this component looks very strange without one
    // and adding border styles by default makes this easier to use in other contexts outside of Matching Guidance
    <div className={cx(!props.isBorderless && 'overflow-clip rounded-lg border border-gray-100')}>
      <AriaButton
        // Note data-[focus-visible]: The CSS example for the RAC Button shows this being used to apply styles to the button when
        // *keyboard* focused, even when click-button focus styles are disabled.
        className={cx(
          'w-full flex gap-4 items-center group hover:cursor-pointer hover:text-brand-100 outline-none data-[focus-visible]:outline data-[focus-visible]:outline-offset-2 data-[focus-visible]:outline-brand-500',
          props.type !== 'custom' && 'p-4 bg-white',
          props.type === 'custom' && props.className,
        )}
        onPress={() => setIsOpen(!props.isOpen)}
      >
        {({ isHovered }) => (
          <>
            {!leftContent && props.icon && (
              <Icon type={props.icon} stroke={isHovered ? 'brand-100' : ''} />
            )}
            {!!leftContent && leftContent}
            {!titleContent && <span className='font-semibold truncate'>{props.title}</span>}
            {!!titleContent && titleContent}
            <div className='ml-auto flex items-center'>
              {!!rightContent && rightContent}
              <Icon
                type={isOpen ? 'chevron-up' : 'chevron-down'}
                stroke={isHovered ? 'brand-100' : ''}
              />
            </div>
          </>
        )}
      </AriaButton>
      {isOpen && (
        <div
          className={cx(
            props.type !== 'custom' && 'bg-gray-50 px-4 py-6 md:px-8 md:py-6',
            !props.isBorderless && 'border-t border-t-gray-100',
          )}
        >
          {props.children}
        </div>
      )}
    </div>
  );
};
